import { FixedIcon } from "../../../atoms/icons/fixed";
import { ArmIcon } from "../../../atoms/icons/arm";
import { addTrailingZeroes } from "../../../util/addTrailingZeroes";
import { dollarFormatter } from "../../../util/dollarFormatter";
import IconButton from "@mui/material/IconButton";
import { EditIcon } from "../../../atoms/icons/edit";
import { InfoIcon } from "../../../atoms/icons/info-i";
import { AccordionItems, ModalAccordion } from "./CustomAccordion";
import Button from "@mui/material/Button";
import React, { useEffect, useMemo, useState, useContext } from "react";
import Chart from "chart.js/auto";
import { getOneTwelfthValue } from "../../../util/getOneTwelfthValue";
import { useClosingCalculations } from "../../../util/hooks/useClosingCalculations";
import {
  InsuranceContainer,
  InsuranceDetailsContainer,
  InterestContainer,
  InterestDetailsContainer,
  PieChartDetailsContainer,
  PmiContainer,
  PmiDetailsContainer,
  PmiTooltipPositioner,
  PrincipalContainer,
  PrincipalDetailsContainer,
  TaxesContainer,
  TaxesDetailsContainer,
} from "./ui-utils";
import { isRefinance } from "../../../util/compare_utils";
import { LookingToContext } from "../../../context/LookingToContext";
import { TooltipCombo } from "./TooltipCombo";
import { styled } from "@mui/material";

const RelativeParent = styled("div")`
  position: relative;
`;

const ToolTipPositioner = styled("div")`
  position: absolute;
  top: -18px;
  left: 10px;
`;

export function LoanDetails({
  details,
  downPayment,
  insurancePaymentMonth,
  plannedClosingDate,
  hasWaivedEscrow,
  insurance,
  earnestMoney,
  credits,
  taxes,
  onEditClick,
  isPrinting = false,
  content,
}) {
  const lookingTo = useContext(LookingToContext);
  const chartCanvasRef = React.useRef();
  const [isLenderFeesExpanded, setIsLenderFeesExpanded] = useState(isPrinting);
  const [isServicesCannotShopForExpanded, setIsServicesCannotShopForExpanded] =
    useState(isPrinting);
  const [isServicesCanShopForExpanded, setIsServicesCanShopForExpanded] =
    useState(isPrinting);
  const [isGovernmentFeesExpanded, setIsGovernmentFeesExpanded] =
    useState(isPrinting);
  const [isPrimaryItemsExpanded, setIsPrimaryItemsExpanded] =
    useState(isPrinting);
  const [isPrePaidsExpanded, setIsPrePaidsExpanded] = useState(isPrinting);
  const [isEscrowsExpanded, setIsEscrowsExpanded] = useState(isPrinting);
  const [isCreditsExpanded, setIsCreditsExpanded] = useState(isPrinting);

  const {
    sumOfCredits,
    sumOfEscrows,
    escrows,
    sumOfPrePaids,
    prePaids,
    sumOfPrimaryItems,
    primaryItems,
    sumOfGovFees,
    governmentFees,
    sumOfCannotShopFor,
    servicesBorrowerCannotShopFor,
    sumOfLenderFees,
    lenderFees,
    titleFees,
    lenderCredit,
    principal,
    interest,
    pmi,
    armFixedTermYears,
    amortizationType,
    loanTerm,
    rate,
    apr,
    monthlyPayment,
    closingCosts,
    totalCashToClose,
  } = useClosingCalculations({
    details,
    downPayment,
    insurancePaymentMonth,
    plannedClosingDate,
    hasWaivedEscrow,
    insurance,
    taxes,
    earnestMoney,
    credits,
    lookingTo,
  });

  const pieChartData = useMemo(
    () => ({
      datasets: [
        {
          data: [
            principal,
            interest,
            pmi,
            getOneTwelfthValue(insurance),
            getOneTwelfthValue(taxes),
          ],
          backgroundColor: [
            "#205590",
            "#e7174b",
            "#600e2a",
            "#3a9ed7",
            "#666666",
          ],
          borderWidth: 0,
        },
      ],
    }),
    [principal, interest, pmi, insurance, taxes]
  );

  useEffect(() => {
    if (!chartCanvasRef.current) return;
    const chart = new Chart(chartCanvasRef.current, {
      type: "doughnut",
      data: pieChartData,
      options: {
        cutout: 0,
        radius: 50,
        plugins: { legend: { display: false } },
      },
    });

    return () => {
      chart.destroy();
    };
  }, [pieChartData, chartCanvasRef]);

  return (
    <>
      <div className="middle-row-top">
        <div
          className={`${
            !armFixedTermYears
              ? "fixed-loan-detail"
              : "fixed-loan-detail arm-loan-detail"
          }`}
        >
          {!armFixedTermYears ? (
            <FixedIcon fill="white" width="36px" height="36px" />
          ) : (
            <ArmIcon fill="white" width="36px" height="36px" />
          )}
          {amortizationType}
        </div>
        <div
          className={`${
            !armFixedTermYears
              ? "thirty-year large"
              : "thirty-year large arm-year"
          }`}
        >
          {!armFixedTermYears ? `${loanTerm} Year` : `${armFixedTermYears}/6`}
        </div>
        <div className="quote-detail-middle-top">
          <span className="bold">Rate</span>
          <span
            className={`large bold ${armFixedTermYears && "arm-card-color"}`}
          >
            {addTrailingZeroes(rate)}%
          </span>
          <span className="bold">APR</span>
          <span
            className={`large bold ${armFixedTermYears && "arm-card-color"}`}
          >
            {addTrailingZeroes(apr)}%
          </span>

          <RelativeParent>
            <ToolTipPositioner>
              <TooltipCombo tip={content.tooltipRateApr} />
            </ToolTipPositioner>
          </RelativeParent>
        </div>
      </div>
      <div className="middle-row-bottom">
        <div className="middle-row-bottom-left">
          <div className="middle-row-title">Monthly Payment</div>
          <div className="left-title">
            <div className="whole-titles-wrapper">
              <div className="titles-wrapper">
                <PrincipalContainer>Principal:</PrincipalContainer>
                <InterestContainer>Interest:</InterestContainer>
                <PmiContainer>
                  PMI:
                  <PmiTooltipPositioner>
                    <TooltipCombo tip={content.tooltip1} />
                  </PmiTooltipPositioner>
                </PmiContainer>
                <InsuranceContainer>Insurance:</InsuranceContainer>
                <TaxesContainer>Taxes:</TaxesContainer>
              </div>
              <div className="titles-wrapper">
                <PrincipalDetailsContainer>
                  {`${dollarFormatter(principal, 2)} /mo`}
                </PrincipalDetailsContainer>
                <InterestDetailsContainer>
                  {`${dollarFormatter(interest, 2)} /mo`}
                </InterestDetailsContainer>
                <PmiDetailsContainer>
                  {`${dollarFormatter(pmi, 2)} /mo`}
                </PmiDetailsContainer>
                <InsuranceDetailsContainer>
                  {`${dollarFormatter(
                    Math.round(Number(insurance ?? 0) / 12)
                  )} /mo`}
                  {!isPrinting && (
                    <IconButton
                      aria-label="Edit loan selections"
                      onClick={onEditClick}
                    >
                      <EditIcon fill="#ddd" width="16px" height="16px" />
                    </IconButton>
                  )}
                </InsuranceDetailsContainer>
                <TaxesDetailsContainer>
                  {`${dollarFormatter(
                    Math.round(Number(taxes ?? 0) / 12)
                  )} /mo`}
                  {!isPrinting && (
                    <IconButton
                      aria-label="Edit loan selections"
                      onClick={onEditClick}
                    >
                      <EditIcon fill="#ddd" width="16px" height="16px" />
                    </IconButton>
                  )}
                </TaxesDetailsContainer>
              </div>
            </div>
            <PieChartDetailsContainer>
              <canvas ref={chartCanvasRef} />
            </PieChartDetailsContainer>
            <div className="i-icon">
              <InfoIcon
                className="margin-right"
                fill="lightgrey"
                width="20px"
                height="20px"
              />
            </div>
          </div>
          <div className="total-closing">
            <div>Total Monthly Payments:</div>
            {dollarFormatter(
              monthlyPayment +
                Number(taxes ?? 0) / 12 +
                Number(insurance ?? 0) / 12,
              2
            )}
          </div>
        </div>
        <div className="middle-row-side">
          <div className="middle-row-bottom-middle">
            <div className="middle-row-title">Closing Costs</div>
            <div className="middle-title">
              <ModalAccordion
                title="Lender Fees"
                amount={sumOfLenderFees}
                expanded={isLenderFeesExpanded}
                onChange={(e, v) => setIsLenderFeesExpanded(v)}
                tooltipText={content.tooltip12}
              >
                <div className="accordion-children">
                  {lenderFees.map((item) => {
                    return (
                      <AccordionItems
                        title={item.title}
                        amount={item.amount}
                        key={item.title}
                      />
                    );
                  })}
                </div>
              </ModalAccordion>
              <ModalAccordion
                title="Services Borrower Cannot Shop For"
                add
                amount={sumOfCannotShopFor}
                expanded={isServicesCannotShopForExpanded}
                onChange={(e, v) => setIsServicesCannotShopForExpanded(v)}
                tooltipText={content.tooltip13}
              >
                <div className="accordion-children">
                  {servicesBorrowerCannotShopFor.map((item) => {
                    return (
                      <AccordionItems
                        title={item.title}
                        amount={item.amount}
                        key={item.title}
                      />
                    );
                  })}
                </div>
              </ModalAccordion>
              <ModalAccordion
                title="Services Borrower Can Shop For"
                add
                amount={titleFees}
                expanded={isServicesCanShopForExpanded}
                onChange={(e, v) => setIsServicesCanShopForExpanded(v)}
                tooltipText={content.tooltip14}
              >
                <div className="accordion-children">
                  <AccordionItems
                    title="Title Fees"
                    amount={titleFees}
                    key="Title Fees"
                  />
                </div>
              </ModalAccordion>
              <ModalAccordion
                title="Government Fees"
                add
                amount={sumOfGovFees}
                expanded={isGovernmentFeesExpanded}
                onChange={(e, v) => setIsGovernmentFeesExpanded(v)}
                tooltipText={content.tooltip15}
              >
                <div className="accordion-children">
                  {governmentFees.map((item) => {
                    return (
                      <AccordionItems
                        title={item.title}
                        amount={item.amount}
                        key={item.title}
                      />
                    );
                  })}
                </div>
              </ModalAccordion>
              <ModalAccordion
                title="Lender Credit"
                subtract={!!lenderCredit}
                add={!lenderCredit}
                amount={Math.abs(lenderCredit)}
                disabled
                tooltipText={content.tooltip16}
              />
              <div className="total-closing">
                <div>Total Closing Costs:</div>
                {dollarFormatter(Math.round(closingCosts))}
              </div>
            </div>
          </div>
          <div className="middle-row-bottom-right">
            <div className="middle-row-title">Cash to Close</div>
            <div className="middle-title">
              <ModalAccordion
                title="Primary Items"
                amount={sumOfPrimaryItems}
                expanded={isPrimaryItemsExpanded}
                onChange={(e, v) => setIsPrimaryItemsExpanded(v)}
                tooltipText={content.tooltip17}
              >
                <div className="accordion-children">
                  {primaryItems.map((item) =>
                    item.title === "Down Payment" && isRefinance() ? null : (
                      <AccordionItems
                        title={item.title}
                        amount={item.amount}
                        key={item.title}
                      />
                    )
                  )}
                </div>
              </ModalAccordion>
              <ModalAccordion
                title="Prepaids"
                amount={sumOfPrePaids}
                add
                expanded={isPrePaidsExpanded}
                onChange={(e, v) => setIsPrePaidsExpanded(v)}
                tooltipText={content.tooltip18}
              >
                <div className="accordion-children">
                  {prePaids.map((item) => {
                    return (
                      <AccordionItems
                        title={item.title}
                        amount={item.amount}
                        key={item.title}
                      />
                    );
                  })}
                </div>
              </ModalAccordion>
              <ModalAccordion
                title="Escrows"
                add
                isLast
                amount={sumOfEscrows}
                expanded={isEscrowsExpanded}
                onChange={(e, v) => setIsEscrowsExpanded(v)}
                tooltipText={content.tooltip19}
              >
                <div className="accordion-children">
                  {escrows.map((item) => {
                    return (
                      <AccordionItems
                        title={item.title}
                        amount={item.amount}
                        key={item.title}
                      />
                    );
                  })}
                </div>
              </ModalAccordion>
              {!isRefinance() && (
                <ModalAccordion
                  title="Credits"
                  subtract
                  isLast
                  amount={sumOfCredits}
                  expanded={isCreditsExpanded}
                  onChange={(e, v) => setIsCreditsExpanded(v)}
                  tooltipText={content.tooltip20}
                >
                  <div className="accordion-children">
                    <AccordionItems
                      title="Earnest Money"
                      amount={earnestMoney}
                      key="Earnest Money"
                    />
                    <AccordionItems
                      title="Seller/Realtor Credit"
                      amount={credits}
                      key="Seller/Realtor Credit"
                    />
                  </div>
                </ModalAccordion>
              )}
              <div className="total-closing">
                <div>Total Cash to Close:</div>
                {dollarFormatter(Math.round(totalCashToClose))}
              </div>
              {!isPrinting && (
                <Button
                  variant="contained"
                  sx={{
                    marginTop: "40px",
                    boxShadow: "none",
                    height: "46px",
                    borderRadius: "8px",
                    backgroundColor: "#e2e2e2",
                    color: "#323a51",
                    paddingLeft: "40px",
                    paddingRight: "40px",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "proxima-nova, sans-serif",
                    fontWeight: "bold",
                    fontSize: "16px",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#868c99",
                    },
                  }}
                  startIcon={<EditIcon fill="#323a51" width="16" height="16" />}
                  onClick={onEditClick}
                >
                  Edit cash to close
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
